import { ChevronLeft, ChevronRight, Clock, Plus, AlertCircle } from 'lucide-react'
import { useState, useMemo, useCallback } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useTimelogs, useTimelogStats, type TimelogWithIssue } from '@/hooks'
import { cn } from '@/lib/utils'

import { TimelogQuickEntry } from './timelog-quick-entry'

/**
 * 日历视图补录功能
 * Requirements: 9.3 - 支持从日历视图快速补录历史工时
 */

interface TimelogCalendarViewProps {
  /** 每日工时目标（小时） */
  dailyGoal?: number
  /** 选中日期回调 */
  onDateSelect?: (date: string) => void
  className?: string
}

interface DayInfo {
  date: number
  dateStr: string
  isCurrentMonth: boolean
  isToday: boolean
  isFuture: boolean
  isWeekend: boolean
  hours: number
  timelogs: TimelogWithIssue[]
}

function getMonthDays(year: number, month: number): Array<{ date: number; isCurrentMonth: boolean }> {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const daysInMonth = lastDay.getDate()
  const startWeekday = firstDay.getDay()

  const days: Array<{ date: number; isCurrentMonth: boolean }> = []

  // 上月填充
  const prevMonthLastDay = new Date(year, month, 0).getDate()
  for (let i = startWeekday - 1; i >= 0; i--) {
    days.push({ date: prevMonthLastDay - i, isCurrentMonth: false })
  }

  // 当月
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ date: i, isCurrentMonth: true })
  }

  // 下月填充到 42 天（6 周）
  const remaining = 42 - days.length
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: i, isCurrentMonth: false })
  }

  return days
}

function formatDateString(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function TimelogCalendarView({
  dailyGoal = 8,
  onDateSelect,
  className,
}: TimelogCalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [showQuickEntry, setShowQuickEntry] = useState(false)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const today = useMemo(() => new Date(), [])
  const todayStr = useMemo(() => formatDateString(today.getFullYear(), today.getMonth(), today.getDate()), [today])

  // 获取当月工时数据
  const startDate = formatDateString(year, month, 1)
  const endDate = formatDateString(year, month, new Date(year, month + 1, 0).getDate())

  const { data: timelogs = [] } = useTimelogs({
    startDate,
    endDate,
    pageSize: 500, // 获取足够多的记录
  })

  const { data: stats } = useTimelogStats({
    startDate,
    endDate,
  })

  // 按日期分组工时
  const timelogsByDate = useMemo(() => {
    const map = new Map<string, TimelogWithIssue[]>()
    timelogs.forEach((timelog) => {
      const existing = map.get(timelog.logDate) ?? []
      existing.push(timelog)
      map.set(timelog.logDate, existing)
    })
    return map
  }, [timelogs])

  // 计算每天的工时小时数
  const hoursByDate = useMemo(() => {
    const map = new Map<string, number>()
    timelogs.forEach((timelog) => {
      const existing = map.get(timelog.logDate) ?? 0
      map.set(timelog.logDate, existing + timelog.minutes / 60)
    })
    return map
  }, [timelogs])

  // 生成日历数据
  const calendarDays = useMemo((): DayInfo[] => {
    const days = getMonthDays(year, month)
    let currentMonth = month
    let currentYear = year

    return days.map((day, index) => {
      // 计算实际的年月
      if (!day.isCurrentMonth) {
        if (index < 7) {
          // 上个月
          currentMonth = month - 1
          currentYear = month === 0 ? year - 1 : year
          if (currentMonth < 0) {currentMonth = 11}
        } else {
          // 下个月
          currentMonth = month + 1
          currentYear = month === 11 ? year + 1 : year
          if (currentMonth > 11) {currentMonth = 0}
        }
      } else {
        currentMonth = month
        currentYear = year
      }

      const dateStr = formatDateString(currentYear, currentMonth, day.date)
      const dayOfWeek = new Date(currentYear, currentMonth, day.date).getDay()
      const dateObj = new Date(currentYear, currentMonth, day.date)

      return {
        date: day.date,
        dateStr,
        isCurrentMonth: day.isCurrentMonth,
        isToday: dateStr === todayStr,
        isFuture: dateObj > today,
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
        hours: hoursByDate.get(dateStr) ?? 0,
        timelogs: timelogsByDate.get(dateStr) ?? [],
      }
    })
  }, [year, month, today, todayStr, hoursByDate, timelogsByDate])

  // 计算本月统计
  const monthStats = useMemo(() => {
    const workDays = calendarDays.filter(
      (d) => d.isCurrentMonth && !d.isWeekend && !d.isFuture
    ).length
    const daysWithTimelog = calendarDays.filter(
      (d) => d.isCurrentMonth && d.hours > 0
    ).length
    const missingDays = calendarDays.filter(
      (d) => d.isCurrentMonth && !d.isWeekend && !d.isFuture && d.hours === 0
    ).length

    return {
      workDays,
      daysWithTimelog,
      missingDays,
      totalHours: stats?.totalHours ?? 0,
    }
  }, [calendarDays, stats])

  const prevMonth = useCallback(() => {
    setCurrentDate(new Date(year, month - 1, 1))
  }, [year, month])

  const nextMonth = useCallback(() => {
    setCurrentDate(new Date(year, month + 1, 1))
  }, [year, month])

  const goToToday = useCallback(() => {
    setCurrentDate(new Date())
  }, [])

  const handleDayClick = useCallback(
    (day: DayInfo) => {
      if (day.isFuture) {return} // 不能记录未来的工时

      setSelectedDate(day.dateStr)
      setShowQuickEntry(true)
      onDateSelect?.(day.dateStr)
    },
    [onDateSelect]
  )

  const handleQuickEntryClose = useCallback(() => {
    setShowQuickEntry(false)
    setSelectedDate(null)
  }, [])

  const getHoursColor = (hours: number, isWeekend: boolean) => {
    if (hours === 0) {
      return isWeekend ? 'text-muted-foreground/50' : 'text-muted-foreground'
    }
    if (hours >= dailyGoal) {
      return 'text-green-600 dark:text-green-400'
    }
    if (hours >= dailyGoal * 0.5) {
      return 'text-amber-600 dark:text-amber-400'
    }
    return 'text-red-600 dark:text-red-400'
  }

  const getHoursBackground = (hours: number, isWeekend: boolean, isFuture: boolean) => {
    if (isFuture || isWeekend) {return ''}
    if (hours === 0) {return 'bg-red-50 dark:bg-red-950/20'}
    if (hours >= dailyGoal) {return 'bg-green-50 dark:bg-green-950/20'}
    if (hours >= dailyGoal * 0.5) {return 'bg-amber-50 dark:bg-amber-950/20'}
    return 'bg-red-50 dark:bg-red-950/20'
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* 头部：月份导航和统计 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold min-w-[120px] text-center">
            {year}年{month + 1}月
          </h2>
          <Button variant="outline" size="icon" onClick={nextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goToToday}>
            今天
          </Button>
        </div>

        {/* 月度统计 */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{monthStats.totalHours.toFixed(1)}h</span>
            <span className="text-muted-foreground">本月</span>
          </div>
          {monthStats.missingDays > 0 && (
            <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-4 w-4" />
              <span>{monthStats.missingDays}天未记录</span>
            </div>
          )}
        </div>
      </div>

      {/* 日历网格 */}
      <div className="rounded-lg border bg-card">
        {/* 星期标题 */}
        <div className="grid grid-cols-7 border-b">
          {['日', '一', '二', '三', '四', '五', '六'].map((day, index) => (
            <div
              key={day}
              className={cn(
                'py-2 text-center text-sm font-medium',
                (index === 0 || index === 6) && 'text-muted-foreground'
              )}
            >
              {day}
            </div>
          ))}
        </div>

        {/* 日期网格 */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => (
            <button
              key={`${day.dateStr}-${index}`}
              onClick={() => handleDayClick(day)}
              disabled={day.isFuture}
              className={cn(
                'relative min-h-[80px] border-b border-r p-2 text-left transition-colors',
                'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset',
                !day.isCurrentMonth && 'bg-muted/30 text-muted-foreground',
                day.isToday && 'ring-2 ring-primary ring-inset',
                day.isFuture && 'cursor-not-allowed opacity-50',
                day.isCurrentMonth && getHoursBackground(day.hours, day.isWeekend, day.isFuture),
                // 移除右边框（最后一列）
                (index + 1) % 7 === 0 && 'border-r-0',
                // 移除底边框（最后一行）
                index >= 35 && 'border-b-0'
              )}
            >
              {/* 日期数字 */}
              <div
                className={cn(
                  'text-sm font-medium',
                  day.isToday &&
                  'inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground',
                  day.isWeekend && !day.isToday && 'text-muted-foreground'
                )}
              >
                {day.date}
              </div>

              {/* 工时显示 */}
              {day.isCurrentMonth && !day.isFuture && (
                <div className="mt-1">
                  {day.hours > 0 ? (
                    <div
                      className={cn(
                        'text-xs font-medium',
                        getHoursColor(day.hours, day.isWeekend)
                      )}
                    >
                      {day.hours.toFixed(1)}h
                    </div>
                  ) : !day.isWeekend ? (
                    <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
                      <Plus className="h-3 w-3" />
                      <span>补录</span>
                    </div>
                  ) : null}
                </div>
              )}

              {/* 工时条目数量指示 */}
              {day.timelogs.length > 0 && (
                <div className="absolute bottom-1 right-1">
                  <div className="flex items-center gap-0.5">
                    {day.timelogs.slice(0, 3).map((_, i) => (
                      <div
                        key={i}
                        className="h-1.5 w-1.5 rounded-full bg-primary"
                      />
                    ))}
                    {day.timelogs.length > 3 && (
                      <span className="text-[10px] text-muted-foreground">
                        +{day.timelogs.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* 图例 */}
      <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded bg-green-100 dark:bg-green-950/40" />
          <span>≥{dailyGoal}h</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded bg-amber-100 dark:bg-amber-950/40" />
          <span>{dailyGoal * 0.5}-{dailyGoal}h</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded bg-red-100 dark:bg-red-950/40" />
          <span>&lt;{dailyGoal * 0.5}h</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
          <span>工时记录</span>
        </div>
      </div>

      {/* 快速补录对话框 */}
      <Dialog open={showQuickEntry} onOpenChange={setShowQuickEntry}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedDate && (
                <>
                  补录工时 - {new Date(selectedDate).toLocaleDateString('zh-CN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long',
                  })}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedDate && (
            <TimelogQuickEntry
              date={selectedDate}
              existingTimelogs={timelogsByDate.get(selectedDate) ?? []}
              onSuccess={handleQuickEntryClose}
              onCancel={handleQuickEntryClose}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
